import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router';
import { clearToastMessages } from 'src/actions';
import { useAccountSelector } from 'src/hooks';
import { MAlert } from 'src/lib';
import { ScreenBreakpoint } from 'src/styles';
import {
  isAccountFinancialInformationComplete,
  shouldDisplayAccountHolderInformation,
  shouldDisplayJointAccountHolderInformation,
} from 'src/utils';

import { MyAccountLayout } from '../../../lib/Layout/MyAccountLayout/MyAccountLayout';
import { MyAccountSidebarMainMenuItemKey } from '../../../lib/Layout/MyAccountLayout/MyAccountSidebar';

import { AccountHolderFinancialEmploymentSection } from './AccountHolderFinancialEmploymentSection';
import { FinancialInformationAssetsSection } from './FinancialInformationAssetsSection';
import { JointAccountHolderFinancialEmploymentSection } from './JointAccountHolderFinancialEmploymentSection';
import { OtherAccountHolderListFinancialEmploymentSection } from './OtherAccountHolderListFinancialEmploymentSection';

export const FinancialInformation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isUpdateAssetsSuccess = useSelector((state: any) => Boolean(state.accounts.updateFinancialAssets.__succeeded));
  const isCreateEmploymentSuccess = useSelector((state: any) =>
    Boolean(state.accountHolders.createFinancialInformationEmployment.__succeeded),
  );
  const isPatchEmploymentSuccess = useSelector((state: any) =>
    Boolean(state.accountHolders.patchFinancialInformationEmployment.__succeeded),
  );

  const { account } = useAccountSelector();

  const isMobile = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.mobile.max})` });

  const hasAnyCreateRequest = () => isUpdateAssetsSuccess || isCreateEmploymentSuccess;
  const hasAnyPatchRequest = () => isUpdateAssetsSuccess || isPatchEmploymentSuccess;
  const hasAnyUpsertRequest = () => hasAnyCreateRequest() || hasAnyPatchRequest();

  const shouldSendAnalyticsEvent = () =>
    !account?.accountId && hasAnyCreateRequest() && isAccountFinancialInformationComplete(account);

  const shouldRedirectToDisclosures = () =>
    !account?.accountId && hasAnyUpsertRequest() && isAccountFinancialInformationComplete(account);

  const shouldDisplayOtherAccountHolderListSection = () => {
    return (
      account?.type.isEntity &&
      account?.isEntityAccountTypeActive &&
      account?.otherAccountHolders &&
      account?.otherAccountHolders?.length > 0
    );
  };

  useEffect(() => {
    if (shouldSendAnalyticsEvent()) {
      window.gtag('event', 'account_financial_information_complete');
    }
  }, [isUpdateAssetsSuccess, isCreateEmploymentSuccess]);

  useEffect(() => {
    if (shouldRedirectToDisclosures()) {
      navigate(`/accounts/disclosures`);
    }
  }, [isUpdateAssetsSuccess, isCreateEmploymentSuccess, isPatchEmploymentSuccess]);

  useEffect(() => {
    return () => {
      dispatch(clearToastMessages());
    };
  }, []);

  return (
    <MyAccountLayout
      sidebarMenuItemKey={MyAccountSidebarMainMenuItemKey.FinancialInformation}
      title={isMobile ? undefined : 'Financial Information'}>
      {account?.lastFailedLinkAttemptError && (
        <MAlert
          type='error'
          description={`You cannot edit account information until your ${account.customTypeLabel} account has been successfully linked.`}
        />
      )}
      <FinancialInformationAssetsSection />
      {shouldDisplayAccountHolderInformation({
        type: account?.type,
        isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
      }) && <AccountHolderFinancialEmploymentSection />}
      {shouldDisplayJointAccountHolderInformation({
        type: account?.type,
        isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
      }) && <JointAccountHolderFinancialEmploymentSection />}
      {shouldDisplayOtherAccountHolderListSection() && <OtherAccountHolderListFinancialEmploymentSection />}
    </MyAccountLayout>
  );
};
